import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { navigation } from '../mock';
import { Button } from './ui/button';
import Logo from './Logo';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState('home');

  // Active section detection
  useEffect(() => {
    const ids = navigation.map((n) => n.href.replace('#', ''));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-slate-200/70">
      {/* Top strip */}
      <div className="hidden lg:block border-b border-slate-100 bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-xs text-slate-600">
          <div className="flex items-center gap-6">
            <a href="tel:01217586892" className="flex items-center gap-1.5 hover:text-emerald-600 transition-colors"><Phone size={12} className="text-emerald-600"/> 0121 758 6892</a>
            <a href="mailto:info@sleekbusiness.co.uk" className="flex items-center gap-1.5 hover:text-emerald-600 transition-colors"><Mail size={12} className="text-emerald-600"/> info@sleekbusiness.co.uk</a>
            <a href="mailto:support@sleekbusiness.co.uk" className="flex items-center gap-1.5 hover:text-emerald-600 transition-colors"><Mail size={12} className="text-emerald-600"/> support@sleekbusiness.co.uk</a>
          </div>
          <div className="flex items-center gap-4">
            <span>Trustpilot 4.9 / 5</span>
            <span className="h-3 w-px bg-slate-300"/>
            <span>Mon–Fri 9:00 – 18:00</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 h-[110px] flex items-center justify-between">
        {/* Logo */}
        <a href="#home" onClick={(e)=>handleClick(e, '#home')} className="flex items-center" aria-label="Sleek Business Home">
          <Logo variant="dark" height={90} />
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navigation.map((item) => {
            const isActive = activeId === item.href.replace('#', '');
            return (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleClick(e, item.href)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors relative ${
                  isActive ? 'text-[#0a2540]' : 'text-slate-600 hover:text-[#0a2540] hover:bg-slate-50'
                }`}
              >
                {item.name}
                {isActive && (
                  <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-teal-500 rounded-full" />
                )}
              </a>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a href="tel:01217586892" className="text-sm font-medium text-slate-700 hover:text-[#0a2540]">Call us</a>
          <Button onClick={(e)=>handleClick(e, '#quote')} className="bg-[#0a2540] hover:bg-[#0a2540]/90 text-white rounded-lg h-10 px-5 text-sm font-semibold">
            Get a Free Quote
          </Button>
        </div>

        {/* Mobile toggle */}
        <button className="lg:hidden p-2 text-[#0a2540]" onClick={() => setOpen(!open)} aria-label="toggle menu" aria-expanded={open}>
          {open ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleClick(e, item.href)}
                className="py-3 text-slate-700 font-medium border-b border-slate-100 last:border-0"
              >
                {item.name}
              </a>
            ))}
            <Button onClick={(e)=>handleClick(e, '#quote')} className="mt-3 bg-[#0a2540] hover:bg-[#0a2540]/90 text-white rounded-lg h-11 w-full">Get a Free Quote</Button>
          </div>
        </div>
      )}
    </header>
  );
}
